import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
interface CarouselProps<T> {
  items: T[];
  getKey: (item: T, index: number) => string;
  renderItem: (item: T, index: number) => React.ReactNode;
  label: string;
  tone?: 'light' | 'dark';
  /** Slide width per breakpoint. Mobile shows one item with a peek of the next. */
  itemClassName?: string;
  /** Long sets get a counter + progress bar instead of dots. */
  indicator?: 'dots' | 'progress';
}
const pad = (n: number) => String(n).padStart(2, '0');

/**
 * Lightweight native scroll-snap carousel: swipe on mobile, arrows on desktop.
 * Must sit inside a `px-5 sm:px-8` container — it bleeds to the screen edge.
 */
export function Carousel<T>({
  items,
  getKey,
  renderItem,
  label,
  tone = 'light',
  itemClassName = 'w-[82%] sm:w-[46%] lg:w-[31.5%]',
  indicator
}: CarouselProps<T>) {
  const trackRef = useRef<HTMLUListElement>(null);
  const [active, setActive] = useState(0);
  const dark = tone === 'dark';
  const mode = indicator ?? (items.length > 7 ? 'progress' : 'dots');
  const stepWidth = useCallback(() => {
    const track = trackRef.current;
    if (!track || track.children.length === 0) return 0;
    const first = track.children[0] as HTMLElement;
    const second = track.children[1] as HTMLElement | undefined;
    return second ? second.offsetLeft - first.offsetLeft : first.offsetWidth;
  }, []);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollTo({
      left: 0
    });
    setActive(0);
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        const step = stepWidth();
        if (!step) return;
        const last = items.length - 1;
        const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
        setActive(atEnd ? last : Math.min(last, Math.round(track.scrollLeft / step)));
      });
    };
    track.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => {
      track.removeEventListener('scroll', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [items, stepWidth]);
  const goTo = (index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(items.length - 1, index));
    track.scrollTo({
      left: clamped * stepWidth(),
      behavior: 'smooth'
    });
  };
  const arrowClass = `flex h-11 w-11 items-center justify-center rounded-full border transition-colors duration-150 ease-out disabled:opacity-30 focus:outline-none focus-visible:ring-4 ${dark ? 'border-ivory/25 text-ivory hover:bg-ivory/10 focus-visible:ring-ember-light/40' : 'border-ink/20 text-ink hover:bg-ink/5 focus-visible:ring-ember/30'}`;
  return <div data-id="element-38" data-mp="3odg7owcglviu">
      <ul ref={trackRef} aria-label={label} className="no-scrollbar -mx-5 flex snap-x snap-mandatory scroll-px-5 gap-4 overflow-x-auto px-5 pb-2 sm:-mx-8 sm:scroll-px-8 sm:gap-5 sm:px-8" data-id="element-39" data-mp="3od6ihaz8nse7">
        {items.map((item, index) => <li key={getKey(item, index)} aria-label={`${index + 1} of ${items.length}`} className={`flex shrink-0 snap-start ${itemClassName}`} data-id="element-40" data-mp="qx7fr58fg76" data-mpi={index}>
            {renderItem(item, index)}
          </li>)}
      </ul>

      <div className="mt-4 flex items-center justify-between gap-4" data-id="element-41" data-mp="3mzf7d8nsqd53">
        {mode === 'dots' ? <div className="flex flex-wrap items-center" data-id="element-42" data-mp="ek4ynhpei9oc">
            {items.map((item, index) => <button key={getKey(item, index)} type="button" onClick={() => goTo(index)} aria-label={`Go to item ${index + 1}`} aria-current={index === active} className="flex h-11 items-center px-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-ember/40" data-id="element-43" data-mp="jipyhg0qqrc9" data-mpi={index}>
                <span className={`block h-[3px] rounded-full transition-[width,background-color] duration-200 ease-out ${index === active ? 'w-6 bg-ember' : `w-3 ${dark ? 'bg-ivory/25' : 'bg-ink/20'}`}`} data-id="element-44" data-mp="129g2j821sk6p" />
              </button>)}
          </div> : <div className="flex min-w-0 flex-1 items-center gap-3" aria-live="polite" data-id="element-45" data-mp="2vsztwdgefcz3">
            <span className={`text-[13px] font-semibold tabular-nums ${dark ? 'text-ivory' : 'text-ink'}`} data-id="element-46" data-mp="3tdm5w39y7wg8">
              {pad(active + 1)}
              <span className={dark ? 'text-ivory/45' : 'text-ink/40'} data-id="element-47" data-mp="3jolzhyt49ys0"> / {pad(items.length)}</span>
            </span>
            <span className={`relative h-[3px] max-w-[180px] flex-1 overflow-hidden rounded-full ${dark ? 'bg-ivory/15' : 'bg-ink/15'}`} aria-hidden data-id="element-48" data-mp="3eozrc3s5b8q2">
              <span className="absolute inset-y-0 left-0 rounded-full bg-ember transition-[width] duration-200 ease-out" style={{
            width: `${(active + 1) / items.length * 100}%`
          }} data-id="element-49" data-mp="b7zmrcxgdg72" />
            </span>
          </div>}
        <div className="flex shrink-0 gap-2" data-id="element-50" data-mp="shld6oekepxi">
          <button type="button" onClick={() => goTo(active - 1)} disabled={active === 0} aria-label="Previous" className={arrowClass} data-id="element-51" data-mp="skzj26mfth9p">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden data-id="element-52" data-mp="16iebzlbwg76f" />
          </button>
          <button type="button" onClick={() => goTo(active + 1)} disabled={active === items.length - 1} aria-label="Next" className={arrowClass} data-id="element-53" data-mp="16gbcmaoux1p1">
            <ChevronRightIcon className="h-5 w-5" aria-hidden data-id="element-54" data-mp="1l7ahpbqhh83v" />
          </button>
        </div>
      </div>
    </div>;
}